<script lang="ts">
  import { defineComponent } from 'vue'

  export default defineComponent({
    name: 'BaseDropdown',
    props: {
      withIcon: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      classDropdown: {
        type: String,
        default: '',
      },
      isRounded: {
        type: Boolean,
        default: false,
      },
      ariaLabel: {
        type: String,
        default: 'Menu',
      },
    },
    data() {
      return {
        isActive: false,
        activeItemIndex: -1,
      }
    },
    computed: {
      dropdownId() {
        return `dropdown-${Math.random().toString(36).substr(2, 9)}`
      },
      buttonId() {
        return `${this.dropdownId}-button`
      },
      menuId() {
        return `${this.dropdownId}-menu`
      },
    },
    mounted() {
      document.addEventListener('keydown', this.handleGlobalKeydown)
    },
    beforeUnmount() {
      document.removeEventListener('keydown', this.handleGlobalKeydown)
    },
    methods: {
      toggleDropdown() {
        if (this.disabled) return
        this.isActive = !this.isActive
        this.activeItemIndex = -1

        if (!this.isActive) {
          this.$nextTick(() => {
            const button = document.getElementById(this.buttonId)
            if (button) button.focus()
          })
        }
      },
      closeDropdown() {
        this.isActive = false
        this.activeItemIndex = -1
      },
      handleGlobalKeydown(event: KeyboardEvent) {
        if (!this.isActive) return

        if (event.key === 'Escape') {
          event.preventDefault()
          this.closeDropdown()
          const button = document.getElementById(this.buttonId)
          if (button) button.focus()
        }
      },
      handleKeydown(event: KeyboardEvent) {
        if (this.disabled) return

        switch (event.key) {
          case 'Enter':
          case ' ':
          case 'ArrowDown':
            event.preventDefault()
            if (!this.isActive) {
              this.isActive = true
              this.activeItemIndex = 0
            } else {
              this.focusNextItem()
            }
            break
          case 'ArrowUp':
            event.preventDefault()
            if (this.isActive) {
              this.focusPreviousItem()
            }
            break
          case 'Escape':
            if (this.isActive) {
              event.preventDefault()
              this.closeDropdown()
            }
            break
        }
      },
      focusNextItem() {
        const items = this.getMenuItems()
        if (items.length === 0) return

        this.activeItemIndex = (this.activeItemIndex + 1) % items.length
        this.focusActiveItem()
      },
      focusPreviousItem() {
        const items = this.getMenuItems()
        if (items.length === 0) return

        this.activeItemIndex = this.activeItemIndex <= 0 ? items.length - 1 : this.activeItemIndex - 1
        this.focusActiveItem()
      },
      focusActiveItem() {
        const items = this.getMenuItems()
        if (items[this.activeItemIndex]) {
          items[this.activeItemIndex].focus()
        }
      },
      getMenuItems() {
        const menu = document.getElementById(this.menuId)
        if (!menu) return []
        return Array.from(menu.querySelectorAll<HTMLElement>('li[tabindex]'))
      },
      handleItemKeydown(event: KeyboardEvent) {
        const target = event.target as HTMLElement
        const button = document.getElementById(this.buttonId)
        switch (event.key) {
          case 'Enter':
          case ' ':
            event.preventDefault()
            if (target) target.click()
            break
          case 'Escape':
            event.preventDefault()
            this.closeDropdown()
            if (button) button.focus()
            break
        }
      },
    },
  })
</script>

<template>
  <div v-if="isActive" class="dropdown-overlay" @click="toggleDropdown" />
  <div class="dropdown">
    <button
      :id="buttonId"
      type="button"
      class="dropdown-button"
      :class="[classDropdown, { 'd-flex': withIcon }]"
      :disabled="disabled"
      :aria-expanded="isActive"
      :aria-haspopup="'menu'"
      :aria-owns="menuId"
      :aria-label="ariaLabel"
      @click="toggleDropdown"
      @keydown="handleKeydown"
    >
      <slot name="button" />
      <BaseIcon
        v-if="withIcon"
        width="15px"
        height="15px"
        icon-name="icon-arrow.svg"
        icon-color-hash="#909090"
        class="ml-4"
        :class="isActive ? 'dropdown-button-arrow-rotate' : 'dropdown-button-arrow'"
        role="img"
        aria-hidden="true"
      />
    </button>
    <transition>
      <ul
        v-if="isActive"
        :id="menuId"
        :class="{ 'dropdown-rounded': isRounded }"
        class="dropdown-list"
        role="menu"
        :aria-labelledby="buttonId"
        @keydown="handleKeydown"
      >
        <div :class="{ 'dropdown-rounded': isRounded }" @click="toggleDropdown">
          <slot name="list" :handle-item-keydown="handleItemKeydown" :menu-id="menuId" />
        </div>
      </ul>
    </transition>
  </div>
</template>

<style scoped lang="scss">
  .dropdown {
    position: relative;

    &-button {
      background: none;
      border: none;
      cursor: pointer;
      padding: 0;

      &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
      }

      &-arrow {
        transition: transform 0.3s ease-in-out;
      }

      &-arrow-rotate {
        transform: rotateX(180deg);
      }
    }

    &-overlay {
      position: fixed;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      z-index: 200;
    }

    &-list {
      position: absolute;
      top: 50px;
      right: 0;
      z-index: 201;
      backdrop-filter: blur(16px);
      list-style: none;
      margin: 0;
      padding: 0;
    }

    &-rounded {
      border-radius: 8px;
    }

    .menu-button {
      background-color: transparent;
      border: 1px solid #50869c;
      color: #50869c;
      border-radius: 7px;
      height: 38px;
      width: 38px;
    }
  }
</style>
